// Composer Studio — 템플릿/데이터 key 형식 검증 + 자동 발급 공용 유틸.
//
// kebab-case 강제는 **템플릿 key만** — URL 라우트 파라미터로 쓰이기 때문(기술 제약).
// 데이터 계열 key(내장 데이터셋·조립 데이터 소스·동적 데이터셋)는 바인딩 flat 키로만 쓰여
// 빈값·점(.)만 금지한다(점은 `{key.field}` 경로 구분자라 불가) — 2026-07-05 사용자 확정(일관 완화).
// kebab은 placeholder 권장 표기로만 유도한다.

/** kebab-case(소문자/숫자, 하이픈 구분) 패턴. 선행/후행/연속 하이픈 불허. */
export const KEBAB_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** kebab 위반 시 표준 에러 문구(toast/인라인 공용). */
export const KEBAB_KEY_ERROR = 'key는 kebab-case(소문자/숫자/하이픈)여야 합니다.';

/** s가 kebab-case key 형식이면 true. */
export function isKebabKey(s: string): boolean {
  return KEBAB_KEY_PATTERN.test(s);
}

/**
 * 데이터 계열 key(내장 데이터셋·조립 데이터 소스·동적 데이터셋 공통) 검증 — 위반 시 에러 문구, 통과 시 null.
 * 규칙: 빈값 금지 + 점(.) 금지(flat 키만). 중복 검사는 각 편집기의 네임스페이스 검사가 담당.
 */
export function flatKeyError(s: string): string | null {
  const trimmed = s.trim();
  if (!trimmed) return '키를 입력하세요.';
  if (trimmed.includes('.')) return '키에 점(.)은 쓸 수 없습니다(flat 키만).';
  return null;
}

/**
 * 데이터셋 칼럼 자동 키 — `field{N}`을 개수+1부터 시작해 **기존 키와 충돌하지 않는 번호**로 발급한다.
 * 개수 기반(`length+1`)만 쓰면 중간 칼럼 삭제 후 재추가 시 기존 키와 같은 키가 생겨 두 칼럼이
 * 같은 저장 값을 미러하는 사고가 난다(2026-08-08 교육일지 실사고). 삭제된 낮은 번호는 재사용하지
 * 않는다 — defaultRows 행에 옛 키의 값이 데이터로 잔존해 새 칼럼이 유령 값을 물려받을 수 있어서다.
 *
 * 발급 규칙을 두 벌로 만들지 않으려고 여기 둔다 — "＋ 칼럼"과 엑셀 가져오기(구성 모드)가
 * 같은 함수를 쓴다.
 */
export function nextAutoFieldKey(
  existingKeys: readonly (string | undefined)[],
  prefix = 'field',
): string {
  const taken = new Set(existingKeys.filter((k): k is string => !!k));
  let n = existingKeys.length + 1;
  while (taken.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}
